import fullstory from '@/plugins/fullstory'
import Laravel from '@/services/Laravel'

export default {
  async findOrCreateOrder({ mmOrderId }) {
    // Parameter validation
    if (!mmOrderId) {
      const error = new Error('Missing mmOrderId for Square order creation')
      fullstory.event('Square Order Validation Error', { hasMmOrderId: false })
      throw error
    }

    try {
      fullstory.event('Finding or Creating Square Order', { mmOrderId })

      const { data: newOrder } = await Laravel.get(
        `/orders/${mmOrderId}/square/find-or-create`
      )
      return newOrder
    } catch (error) {
      fullstory.event('Square Order Exception', {
        mmOrderId,
        status: error.response?.status,
        errorMessage: error.message,
        stack: error.stack,
      })

      // Preserve original error for caller handling
      throw error
    }
  },

  // Call this function to send a payment token, buyer name, and other details
  // to the server to create a Payment with the Payments API
  async createPayment({ squareToken, squareOrderId, mmOrderId }) {
    // Parameter validation
    if (!squareToken || !squareOrderId || !mmOrderId) {
      const error = new Error('Missing required parameters for Square payment')
      fullstory.event('Square Payment Validation Error', {
        hasToken: !!squareToken,
        hasSquareOrderId: !!squareOrderId,
        hasMmOrderId: !!mmOrderId,
      })
      throw error
    }

    let payload = {
      sourceId: squareToken,
      squareOrderId,
    }

    try {
      // Log payment creation intent
      fullstory.event('Creating Square Payment', {
        mmOrderId,
        squareOrderId,
        hasToken: true, // Don't log actual token
      })

      const paymentResponse = await Laravel.post(
        `/orders/${mmOrderId}/square/pay`,
        payload
      )

      if (paymentResponse.status === 200) {
        return paymentResponse.data
      }

      // Log non-200 success responses (shouldn't happen but defensive)
      fullstory.event('Square Payment Unexpected Response', {
        status: paymentResponse.status,
        hasData: !!paymentResponse.data,
      })
      throw new Error(
        `Unexpected response from Square API: ${paymentResponse.status}`
      )
    } catch (error) {
      // Enhanced error logging and re-throw with details
      const errorData = error.response?.data
      const errorStatus = error.response?.status

      // Log detailed error for debugging
      fullstory.event('Square Payment Exception', {
        mmOrderId,
        squareOrderId,
        status: errorStatus,
        errorMessage: error.message,
        errorData: errorData,
        stack: error.stack,
      })

      // Check for "already paid" edge case
      if (errorData === 'The order is already paid.') {
        // Re-throw as-is, component handles this specifically
        throw error
      }

      // Preserve error structure for component consumption
      // Component expects error.response.data.code and error.response.data.detail
      if (!error.response) {
        // Network error or other non-response error
        const networkError = new Error(
          `Network error creating Square payment: ${error.message}`
        )
        networkError.response = {
          data: {
            code: 'NETWORK_ERROR',
            detail: error.message,
          },
        }
        throw networkError
      }

      // API returned an error response - re-throw with preserved structure
      throw error
    }
  },
}
